import { Handle, Position } from '@xyflow/react';
import { User, ChevronDown, ChevronRight, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ENTITY_ICONS } from '@/constants/entityIcons';
import { Badge, getColorSchemeFromString, UserCard } from '@bliks-io/ui';
import type { DepartmentNodeData } from '../utils/buildOrganigramLayout';

export function DepartmentNode({ data }: { data: DepartmentNodeData }) {
  const { t } = useTranslation();

  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (data.onToggleExpand) {
      data.onToggleExpand(data.departmentId);
    }
  };

  const memberCount = data.members?.length || 0;
  const hasMembers = memberCount > 0;

  return (
    <div className="bg-white border border-line rounded-lg shadow-xs hover:border-line-strong hover:shadow-md transition-all w-[280px]">
      <Handle type="target" position={Position.Top} className="w-2 h-2 bg-fill-neutral!" />

      {/* Header */}
      <div className="px-4 py-3 border-b border-line-subtle">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <ENTITY_ICONS.department className="w-4 h-4 text-fg-subtle shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-foreground truncate">{data.name}</div>
            </div>
          </div>
          {data.abbreviation && (
            <Badge
              shape="rounded"
              colorScheme={getColorSchemeFromString(data.departmentId)}
              className="text-xs"
            >
              {data.abbreviation}
            </Badge>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Manager */}
        <div>
          <div className="text-xs text-muted-foreground mb-1.5">
            {t('organization.organigram.manager')}
          </div>
          {data.manager ? (
            <UserCard
              user={{
                firstName: data.manager.firstName,
                lastName: data.manager.lastName,
                email: data.manager.email,
                role: data.manager.role,
                avatar: data.manager.avatar
              }}
              variant="with-role"
              className="border-0 p-0"
            />
          ) : (
            <div className="flex items-center gap-3 text-fg-subtle">
              <div className="h-9 w-9 rounded-full bg-surface-subtle flex items-center justify-center">
                <User className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-fg-subtle">
                  {t('organization.organigram.notAssigned')}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Collapsible Members Section */}
        {data.isExpanded && hasMembers && (
          <div
            className="pt-3 border-t border-line-subtle space-y-2 max-h-[200px] overflow-y-auto"
            onWheelCapture={(e) => e.stopPropagation()}
          >
            {data.members?.map((member, index) => (
              <div key={member._id} className={index > 0 ? 'pt-2 border-t border-line-subtle' : ''}>
                <UserCard
                  user={{
                    firstName: member.firstName,
                    lastName: member.lastName,
                    email: member.email,
                    role: member.role,
                    avatar: member.avatar
                  }}
                  variant="with-role"
                  className="border-0 p-0"
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t border-line-subtle bg-surface-subtle rounded-b-lg">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">
            {t('organization.organigram.level')} {data.level}
          </span>
          {hasMembers && (
            <button
              onClick={handleToggleExpand}
              className="flex items-center gap-2 text-fg-secondary hover:text-foreground transition-colors"
            >
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                {t('organization.organigram.memberCount', { count: memberCount })}
              </span>
              {data.isExpanded ? (
                <ChevronDown className="w-3 h-3" />
              ) : (
                <ChevronRight className="w-3 h-3" />
              )}
            </button>
          )}
        </div>
      </div>

      <Handle type="source" position={Position.Bottom} className="w-2 h-2 bg-fill-neutral!" />
    </div>
  );
}
